import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SharedService } from '../../shared/shared.service';
import {
  ChangeTaskStatusRequest,
  CompleteTaskRequest,
  CreateTaskRequest,
  CreateTaskStatusRequest,
  CreateTaskTypeGroupRequest,
  CreateTaskTypeRequest,
  EntityResolveResponse,
  PaginatedTasksResponse,
  TaskEntityLinkResponse,
  TaskFilters,
  TaskHistoryResponse,
  TaskStatus,
  TaskType,
  TaskTypeGroup,
  TaskWithDetailsResponse,
  UpdateTaskRequest,
  UpdateTaskStatusRequest,
  UpdateTaskTypeGroupRequest,
  UpdateTaskTypeRequest,
} from '../models/task.models';
@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private baseUrl = `${environment.fastApiUrl}/api/tasks`;

  constructor( private sharedService: SharedService ) {}

  /**
   * Create a new task
   */
  createTask(request: CreateTaskRequest): Observable<any> {
    return this.sharedService
      .postFastApiDataWithHeaders(this.baseUrl, request)
      .pipe(map((response) => response.body));
  }

  getTaskById(id: number): Observable<TaskWithDetailsResponse> {
    return this.sharedService.getFastApiData(`${this.baseUrl}/${id}`);
  }
  /**
   * Get task by ID with full details
   */
  getTask(taskId: number): Observable<TaskWithDetailsResponse> {
    return this.sharedService.getFastApiData(`${this.baseUrl}/${taskId}`).pipe(
      map((response) => {
        const task = response.task;
        console.log('Mapped task with entities:', task.entities);
        return task;
      })
    );
  }

  /**
   * Update task
   */
  updateTask(taskId: number, request: UpdateTaskRequest): Observable<any> {
    return this.sharedService
      .putFastApiData(`${this.baseUrl}/${taskId}`, request)
      .pipe(map((response) => response.body));
  }

  /**
   * Delete task (soft delete)
   */
  deleteTask(taskId: number): Observable<any> {
    return this.sharedService
      .deleteFastApiData(`${this.baseUrl}/${taskId}`)
      .pipe(map((response) => response.body));
  }

  /**
   * Search tasks with filters and pagination
   */
  searchTasks(
    filters?: TaskFilters,
    page: number = 1,
    pageSize: number = 50,
    sortBy: string = 'created_at',
    sortOrder: 'asc' | 'desc' = 'desc',
    kanbanMode?: boolean,
    includeItems?: boolean
  ): Observable<PaginatedTasksResponse> {

    let url = `${this.baseUrl}?page=${page}&page_size=${pageSize}&sort_by=${sortBy}&sort_order=${sortOrder}`;

    if (kanbanMode !== undefined) {
      url += `&kanban_mode=${kanbanMode}`;
    }
    if (includeItems !== undefined) {
      url += `&include_items=${includeItems}`;
    }

    const appendParam = (key: string, value: any) => {
      if (Array.isArray(value)) {
        value.forEach(v => {
          url += `&${key}=${v}`;
        });
      } else if (value !== null && value !== undefined) {
        url += `&${key}=${value}`;
      }
    };

    if (filters) {
      appendParam('status_id', filters.status_id);
      appendParam('task_type_id', filters.task_type_id);
      appendParam('group_id', filters.group_id);
      appendParam('assigned_to', filters.assigned_to);
      appendParam('created_by', filters.created_by);
      appendParam('priority', filters.priority);
      appendParam('entity_type', filters.entity_type);
      appendParam('entity_id', filters.entity_id);
      appendParam('project_id', filters.project_id);

      if (filters.is_overdue !== undefined) url += `&is_overdue=${filters.is_overdue}`;
      if (filters.is_closed !== undefined) url += `&is_closed=${filters.is_closed}`;
      if (filters.include_deleted !== undefined) url += `&include_deleted=${filters.include_deleted}`;

      appendParam('due_after', filters.due_after);
      appendParam('due_before', filters.due_before);
      appendParam('created_after', filters.created_after);
      appendParam('created_before', filters.created_before);
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
    }

    return this.sharedService.getFastApiData(url);
  }

  /**
   * Get tasks assigned to current user
   */
  getMyTasks(
    isClosed: boolean = false,
    page: number = 1,
    pageSize: number = 500,
    kanbanMode?: boolean
  ): Observable<PaginatedTasksResponse> {
    let url = `${this.baseUrl}/my-tasks?is_closed=${isClosed}&page=${page}&page_size=${pageSize}`;
    if (kanbanMode !== undefined) {
      url += `&kanban_mode=${kanbanMode}`;
    }
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Get all tasks linked to a specific entity
   */
  getTasksByEntity(
    entityType: string,
    entityId: string,
    isClosed?: boolean
  ): Observable<PaginatedTasksResponse> {
    let url = `${this.baseUrl}/entity/${entityType}/${entityId}`;
    if (isClosed !== undefined) {
      url += `?is_closed=${isClosed}`;
    }
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Change task status
   */
  changeStatus(taskId: number, request: ChangeTaskStatusRequest): Observable<any> {
    return this.sharedService
      .postFastApiDataWithHeaders(`${this.baseUrl}/${taskId}/status`, request)
      .pipe(map((response) => response.body));
  }

  /**
   * Complete task
   */
  completeTask(
    taskId: number,
    completedStatusId?: number,
    request?: CompleteTaskRequest
  ): Observable<any> {
    let url = `${this.baseUrl}/${taskId}/complete`;
    if (completedStatusId) {
      url += `?completed_status_id=${completedStatusId}`;
    }
    return this.sharedService
      .postFastApiDataWithHeaders(url, request || {})
      .pipe(map((response) => response.body));
  }

  /**
   * Get task history
   */
  getTaskHistory(taskId: number): Observable<TaskHistoryResponse[]> {
    return this.sharedService.getFastApiData(`${this.baseUrl}/${taskId}/history`);
  }

  /**
   * Get task comments (using shared comments system)
   */
  getTaskComments(taskId: number, includeDeleted: boolean = false): Observable<any[]> {
    const url = `${this.baseUrl}/${taskId}/comments?include_deleted=${includeDeleted}`;
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Add comment to task (using shared comments system)
   */
  addTaskComment(taskId: number, commentData: any): Observable<any> {
    return this.sharedService
      .postFastApiDataWithHeaders(`${this.baseUrl}/${taskId}/comments`, commentData)
      .pipe(map((response) => response.body));
  }

  /**
   * Update comment
   */
  updateTaskComment(commentId: number, commentData: any): Observable<any> {
    return this.sharedService
      .putFastApiData(`${this.baseUrl}/comments/${commentId}`, commentData)
      .pipe(map((response) => response.body));
  }

  /**
   * Delete comment (soft delete)
   */
  deleteTaskComment(commentId: number): Observable<any> {
    return this.sharedService
      .deleteFastApiData(`${this.baseUrl}/comments/${commentId}`)
      .pipe(map((response) => response.body));
  }

  /**
   * Add task to project(s)
   */
  addTaskToProjects(taskId: number, projectIds: number[]): Observable<any> {
    return this.sharedService
      .postFastApiDataWithHeaders(`${this.baseUrl}/${taskId}/projects`, { project_ids: projectIds })
      .pipe(map((response) => response.body));
  }

  /**
   * Remove task from project
   */
  removeTaskFromProject(taskId: number, projectId: number): Observable<any> {
    return this.sharedService
      .deleteFastApiData(`${this.baseUrl}/${taskId}/projects/${projectId}`)
      .pipe(map((response) => response.body));
  }

  /**
   * Get task entity links
   */
  getTaskEntities(taskId: number): Observable<TaskEntityLinkResponse[]> {
    return this.sharedService.getFastApiData(`${this.baseUrl}/${taskId}/entities`);
  }

  /**
   * Add entity link to task
   */
  addTaskEntity(taskId: number, entityData: any): Observable<any> {
    return this.sharedService
      .postFastApiDataWithHeaders(`${this.baseUrl}/${taskId}/entities`, entityData)
      .pipe(map((response) => response.body));
  }

  /**
   * Remove entity link from task
   */
  removeTaskEntity(taskId: number, entityLinkId: number): Observable<any> {
    return this.sharedService
      .deleteFastApiData(`${this.baseUrl}/${taskId}/entities/${entityLinkId}`)
      .pipe(map((response) => response.body));
  }

  /**
   * Get available entity types from the entity registry
   */
  getEntityTypes(includeInactive: boolean = false): Observable<
    {
      entity_type: string;
      description: string;
      display_name: string;
      icon: string;
      color: string;
      is_active: boolean;
    }[]
  > {
    const url = `${environment.fastApiUrl}/api/entity-registry?include_inactive=${includeInactive}`;
    return this.sharedService
      .getFastApiData(url)
      .pipe(map((response: any) => response.entity_types || []));
  }

  /**
   * Resolve a single entity link to its display name, icon, color, and navigation routes
   */
  resolveEntityLink(entityType: string, entityId: string): Observable<EntityResolveResponse> {
    const url = `${environment.fastApiUrl}/api/entity-registry/${entityType}/${entityId}/resolve`;
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Search entities of a given type by name (for autocomplete)
   */
  searchEntityType(
    entityType: string,
    query: string,
    limit: number = 10
  ): Observable<{ items: { id: string; display_name: string }[] }> {
    const url = `${environment.fastApiUrl}/api/entity-registry/${entityType}/search?q=${encodeURIComponent(query)}&limit=${limit}`;
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Get user info by ID for task assignees
   */
  getUserInfo(userId: number): Observable<any> {
    return this.sharedService.getFastApiData(
      `${environment.fastApiUrl}/auth/userinfo?user_id=${userId}`
    );
  }

  // ==================== Settings: Type Groups ====================

  getTaskTypeGroups(isActive?: boolean): Observable<TaskTypeGroup[]> {
    const params = isActive !== undefined ? `?is_active=${isActive}` : '';
    return this.sharedService.getFastApiData(
      `${environment.fastApiUrl}/api/task-type-groups${params}`
    );
  }

  createTaskTypeGroup(request: CreateTaskTypeGroupRequest): Observable<TaskTypeGroup> {
    return this.sharedService.postFastApiDataWithHeaders(
      `${environment.fastApiUrl}/api/task-type-groups`,
      request
    ) as any;
  }

  updateTaskTypeGroup(
    groupId: number,
    request: UpdateTaskTypeGroupRequest
  ): Observable<TaskTypeGroup> {
    return this.sharedService.putFastApiData(
      `${environment.fastApiUrl}/api/task-type-groups/${groupId}`,
      request
    ) as any;
  }

  deleteTaskTypeGroup(groupId: number): Observable<any> {
    return this.sharedService.deleteFastApiData(
      `${environment.fastApiUrl}/api/task-type-groups/${groupId}`
    );
  }

  // ==================== Settings: Task Types ====================

  getTaskTypes(groupId?: number, isActive?: boolean): Observable<TaskType[]> {
    let url = `${environment.fastApiUrl}/api/task-types`;
    const params: string[] = [];
    if (groupId !== undefined) params.push(`group_id=${groupId}`);
    if (isActive !== undefined) params.push(`is_active=${isActive}`);
    if (params.length) url += '?' + params.join('&');
    return this.sharedService.getFastApiData(url);
  }

  createTaskType(request: CreateTaskTypeRequest): Observable<TaskType> {
    return this.sharedService.postFastApiDataWithHeaders(
      `${environment.fastApiUrl}/api/task-types`,
      request
    ) as any;
  }

  updateTaskType(typeId: number, request: UpdateTaskTypeRequest): Observable<TaskType> {
    return this.sharedService.putFastApiData(
      `${environment.fastApiUrl}/api/task-types/${typeId}`,
      request
    ) as any;
  }

  deleteTaskType(typeId: number): Observable<any> {
    return this.sharedService.deleteFastApiData(
      `${environment.fastApiUrl}/api/task-types/${typeId}`
    );
  }

  // ==================== Settings: Task Statuses ====================

  /**
   * Get the active clinic for a patient or provider entity.
   * Returns { entity_type, entity_id, clinic_id } — clinic_id may be null.
   */
  getEntityActiveClinic(
    entityType: string,
    entityId: string
  ): Observable<{ entity_type: string; entity_id: string; clinic_id: number | null }> {
    const url = `${environment.fastApiUrl}/api/entity-registry/${entityType}/${entityId}/active-clinic`;
    return this.sharedService.getFastApiData(url);
  }

  getTaskStatuses(groupId?: number, isActive?: boolean): Observable<TaskStatus[]> {
    let url = `${environment.fastApiUrl}/api/task-statuses`;
    const params: string[] = [];
    if (groupId !== undefined) params.push(`group_id=${groupId}`);
    if (isActive !== undefined) params.push(`is_active=${isActive}`);
    if (params.length) url += '?' + params.join('&');
    return this.sharedService.getFastApiData(url);
  }

  createTaskStatus(request: CreateTaskStatusRequest): Observable<TaskStatus> {
    return this.sharedService.postFastApiDataWithHeaders(
      `${environment.fastApiUrl}/api/task-statuses`,
      request
    ) as any;
  }

  updateTaskStatus(statusId: number, request: UpdateTaskStatusRequest): Observable<TaskStatus> {
    return this.sharedService.putFastApiData(
      `${environment.fastApiUrl}/api/task-statuses/${statusId}`,
      request
    ) as any;
  }

  deleteTaskStatus(statusId: number): Observable<any> {
    return this.sharedService.deleteFastApiData(
      `${environment.fastApiUrl}/api/task-statuses/${statusId}`
    );
  }
}
